import React from 'react';
import { Radio, Popover } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio/interface';
import { Link, withRouter, RouteComponentProps } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { IPackage, IRoomsContent, RefundableEnum } from 'src/common-types/index';
import { BlueButton, ModalCancellationPolicies } from 'src/components/components/index';
import {
  ManDarkSvg,
  BedSmallSvg,
  InfoSvg,
  NoImageSvg,
  ArrowBottomSvg,
  ArrowTopSvg,
  ImagesQuantitySvg,
} from 'src/assets/index';
import { Routes } from 'src/constants/index';
import { renderNumber } from 'src/utils/index';
import { HotelImagesSlider } from '../hotel-image-slider';
import './style.scss';

interface IProps extends RouteComponentProps {
  groupedPackages: IPackage[];
  getRoomContent: (room: string) => IRoomsContent;
  nights: number;
  hotelId: number;
}

interface IState {
  isAllAmenitiesShown: boolean;
  selectedPackageId: string;
  additionalHeight: number;
  showImageSlider: boolean;
  isModalCancellationPoliciesVisible: boolean;
  cancellationPoliciesText: string;
  refundabilityText: string;
  isChangedCancellationPolicy: boolean;
  nameHeight: number;
}

const zero = 0;
const one = 1;
const five = 5;
const basisHeight = 18;
const basisPadding = 60;
const basisMargin = 14;
const timeoutShow = 5000;

class RoomComponent extends React.Component<IProps, IState> {
  private timeout: number;
  state: IState;
  wrapperRef: React.RefObject<HTMLDivElement> = React.createRef();
  roomRef: React.RefObject<HTMLDivElement> = React.createRef();

  constructor(props: IProps) {
    super(props);
    const currentPackage = props.groupedPackages[zero];

    this.state = {
      isAllAmenitiesShown: false,
      selectedPackageId: currentPackage.packageId,
      additionalHeight: 0,
      showImageSlider: false,
      isModalCancellationPoliciesVisible: false,
      cancellationPoliciesText: currentPackage.cancellationPolicyText,
      refundabilityText: currentPackage.refundabilityText ? currentPackage.refundabilityText : '',
      isChangedCancellationPolicy: false,
      nameHeight: 0,
    };
  }

  openImageSlider = () => {
    this.setState({ showImageSlider: true });
  };

  closeImageSlider = () => {
    this.setState({ showImageSlider: false });
  };

  toggleShowAll = (): void => {
    this.setState((prevState: IState) => ({ isAllAmenitiesShown: !prevState.isAllAmenitiesShown }));
  };

  handleClickOutside = (event: MouseEvent): void => {
    if (this.wrapperRef && !this.wrapperRef?.current?.contains(event.target as Node)) {
      this.setState({ isAllAmenitiesShown: false });
    }
  };

  componentDidMount(): void {
    document.addEventListener('mousedown', this.handleClickOutside);

    if (this.roomRef && this.roomRef.current) {
      const rooms = document.querySelectorAll('.rooms-search__room');

      if (rooms && rooms.length) {
        const { offsetHeight } = this.roomRef.current;
        let maxHeight = offsetHeight;

        rooms.forEach((item: HTMLDivElement) => {
          if (item.offsetHeight > maxHeight) {
            maxHeight = item.offsetHeight;
          }
        });

        if (maxHeight > offsetHeight) {
          this.setState({ additionalHeight: maxHeight - offsetHeight });
        }
      }

      const names = document.querySelectorAll('.rooms-search__name-wrapper');

      if (names && names.length) {
        let maxHeight = 0;

        names.forEach((item: HTMLDivElement) => {
          if (item.scrollHeight > maxHeight) {
            maxHeight = item.scrollHeight;
          }
        });

        this.setState({ nameHeight: maxHeight });
      }
    }
  }

  componentWillUnmount(): void {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  onRoomSelect = (e: RadioChangeEvent): void => {
    this.setState({ selectedPackageId: e.target.value, isChangedCancellationPolicy: true });

    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    // @ts-ignore
    this.timeout = setTimeout(() => {
      this.setState({ isChangedCancellationPolicy: false });
    }, timeoutShow);
  };

  getReserveRoomUrl = (selectedPackageId: string): string => {
    const { hotelId } = this.props;

    return `${Routes.Book}/${hotelId}/${selectedPackageId}${location.search}`;
  };

  onCancelSlider = (e: React.MouseEvent<HTMLElement, MouseEvent>, isReserveRoom?: boolean) => {
    this.closeImageSlider();

    if (isReserveRoom) {
      const { history } = this.props;
      const { selectedPackageId } = this.state;

      history.push(this.getReserveRoomUrl(selectedPackageId));
    }
  };

  showCancellationPolicies = (): void => {
    this.setState({
      isModalCancellationPoliciesVisible: true,
      cancellationPoliciesText: this.getPackageInfo().cancellationPolicyText,
      refundabilityText: this.getPackageInfo().refundabilityText,
    });
  };

  handleCancelModalCancellationPolicies = (): void => {
    this.setState({ isModalCancellationPoliciesVisible: false });
  };

  getPackageInfo = () => {
    return this.props.groupedPackages.find((item) => {
      return item.packageId === this.state.selectedPackageId;
    });
  };

  render(): React.ReactNode {
    const {
      isAllAmenitiesShown,
      selectedPackageId,
      additionalHeight,
      showImageSlider,
      isModalCancellationPoliciesVisible,
      cancellationPoliciesText,
      refundabilityText,
      isChangedCancellationPolicy,
    } = this.state;
    const { groupedPackages, nights, getRoomContent } = this.props;
    const currentPackage = groupedPackages[zero];
    const { packageId, rooms, refundability, price, pricePerNight } = currentPackage;
    const selectedPackage = groupedPackages.find(
      ({ packageId }) => packageId === selectedPackageId,
    );
    const selectedRoom = selectedPackage?.rooms[zero];
    const firstRoom = rooms[zero];
    const allRooms = groupedPackages.map(({ rooms, priceDifference, packageId }) => {
      return {
        packageId,
        priceDifference,
        room: rooms[zero],
      };
    });
    const currentBasisHeight =
      basisPadding + basisHeight * allRooms.length + basisMargin * (allRooms.length - one);
    const basisMinHeight = currentBasisHeight + additionalHeight;

    if (!firstRoom) {
      return null;
    }

    const { bedType, roomName, adultsCount } = firstRoom;
    const adults = new Array(adultsCount).fill(one);
    const content = getRoomContent(selectedRoom.targetRoomKey);
    const numberOfRooms = groupedPackages[zero].rooms.length;

    return (
      <div className="rooms-search__room" key={packageId} ref={this.roomRef}>
        <div className="rooms-search__label-info">
          <p className="rooms-search__label-text">
            <FormattedMessage id="avg.rate" />
          </p>
          <p className="rooms-search__label-price">${renderNumber(pricePerNight)}</p>
          <p className="rooms-search__label-text">
            <FormattedMessage id="per.night" />
          </p>
        </div>
        {content.images.length ? (
          <div className="rooms-search__room-image">
            <img
              className="rooms-search__room-img"
              src={content.images[zero]}
              alt="room image"
              onClick={this.openImageSlider}
            />
            <div className="rooms-search__room-image-count" onClick={this.openImageSlider}>
              <ImagesQuantitySvg />
              <span className="rooms-search__images-quantity-text">{content.images.length}+</span>
            </div>
          </div>
        ) : (
          <div className="rooms-search__room-image no-image">
            <NoImageSvg />
          </div>
        )}
        <div
          className="rooms-search__name-wrapper"
          style={{ height: `${this.state.nameHeight}px` }}
        >
          <div className="rooms-search__name">{roomName}</div>
          <div className="rooms-search__guests">
            {adults.map((value, i) => {
              return (
                <span className="rooms-search__icon-wrapper" key={i}>
                  <ManDarkSvg />
                </span>
              );
            })}
          </div>
        </div>
        <div className="rooms-search__bed">
          <BedSmallSvg /> {bedType ? bedType : <FormattedMessage id="no.data.available" />}
        </div>
        <div className="rooms-search__amenities">
          {content.amenities.length ? (
            <div ref={this.wrapperRef}>
              <div className="rooms-search__amenities-text">
                {content.amenities.slice(zero, five).join(', ')}
              </div>
              <div className="rooms-search__amenities-more" onClick={this.toggleShowAll}>
                <FormattedMessage id="more.details" />{' '}
                {isAllAmenitiesShown ? <ArrowTopSvg /> : <ArrowBottomSvg />}
              </div>
              {isAllAmenitiesShown && (
                <div className="rooms-search__amenities-all">
                  {content.amenities.slice(five).map((amenity) => {
                    return <div key={amenity}>{amenity}</div>;
                  })}
                </div>
              )}
            </div>
          ) : (
            <span className="rooms-search__amenities-no-data">
              <FormattedMessage id="no.info.amenities" />
            </span>
          )}
        </div>
        {allRooms && allRooms.length ? (
          <div className="rooms-search__basis-wrapper" style={{ minHeight: `${basisMinHeight}px` }}>
            <Radio.Group onChange={this.onRoomSelect} value={selectedPackageId}>
              {allRooms.map(({ room, packageId, priceDifference }) => {
                return (
                  <Radio value={packageId} key={packageId}>
                    <div className="rooms-search__basis">{room.roomBasis}</div>
                    <div className="rooms-search__price-difference">
                      {priceDifference ? (
                        `+ $${renderNumber(Math.floor(priceDifference))}`
                      ) : (
                        <span className="rooms-search__no-difference">
                          <FormattedMessage id="no.additional.charge" />
                        </span>
                      )}
                    </div>
                  </Radio>
                );
              })}
            </Radio.Group>
          </div>
        ) : (
          <div style={{ minHeight: `${basisMinHeight}px` }} />
        )}
        {refundability !== RefundableEnum.Unknown ? (
          <div className="rooms-search__refundable">
            {refundability === RefundableEnum.Refundable && (
              <div className="rooms-search__refundable-ref" onClick={this.showCancellationPolicies}>
                <Popover
                  overlayClassName="overlay-popover"
                  content={<FormattedMessage id="changed.cancellation.policy" />}
                  visible={isChangedCancellationPolicy}
                  placement="bottomLeft"
                >
                  <FormattedMessage id="refundable" />
                  <InfoSvg />
                </Popover>
              </div>
            )}
            {refundability === RefundableEnum.Nonrefundable && (
              <div
                className="rooms-search__refundable-nonref"
                onClick={this.showCancellationPolicies}
              >
                <Popover
                  overlayClassName="overlay-popover"
                  content={<FormattedMessage id="changed.cancellation.policy" />}
                  visible={isChangedCancellationPolicy}
                  placement="bottomLeft"
                >
                  <FormattedMessage id="non.refundable" />
                  <InfoSvg />
                </Popover>
              </div>
            )}
          </div>
        ) : null}
        <div className="rooms-search__bottom">
          <div className="rooms-search__price">
            <div className="hotel-card-wrapper__price-total">
              <p className="hotel-card-wrapper__price-total-text">
                ${renderNumber(Math.floor(price))}
              </p>
              <div className="hotel-card-wrapper__for-info">
                <p className="hotel-card-wrapper__for-info-text">
                  <FormattedMessage id="for" />{' '}
                  <FormattedMessage id="nights" values={{ count: nights }} />{' '}
                  <span className="rooms-search__price-includes-taxes">
                    <FormattedMessage id="abbreviated.price.includes.taxes.fees" />
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div className={`rooms-search__action ${numberOfRooms !== one ? 'more-rooms' : ''}`}>
            <Link to={this.getReserveRoomUrl(selectedPackageId)}>
              <BlueButton>
                {numberOfRooms === one ? (
                  <FormattedMessage id="reserve" />
                ) : (
                  <FormattedMessage id="reserve.count.rooms" values={{ count: numberOfRooms }} />
                )}
              </BlueButton>
            </Link>
          </div>
        </div>
        <HotelImagesSlider
          onCancel={this.onCancelSlider}
          visible={showImageSlider}
          images={content.images.map((img: string) => {
            return {
              description: 'room-image',
              url: img,
              height: zero,
              width: zero,
            };
          })}
          hotelName={roomName}
          startIndex={zero}
        />
        <ModalCancellationPolicies
          cancellationPoliciesText={cancellationPoliciesText}
          refundabilityText={refundabilityText}
          visible={isModalCancellationPoliciesVisible}
          onCancel={this.handleCancelModalCancellationPolicies}
        />
      </div>
    );
  }
}

export const Room = withRouter(RoomComponent);
